import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, Truck, X, ExternalLink, Filter } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Navigate } from "react-router-dom";

const STATUS_COLORS = {
  paid:      { bg: "bg-[#8FA88B]/20",  text: "text-[#8FA88B]",  label: "Paid" },
  shipped:   { bg: "bg-[#C0953A]/25",  text: "text-[#DCBB6C]",  label: "Shipped" },
  delivered: { bg: "bg-[#5b8a5f]/25",  text: "text-[#8FA88B]",  label: "Delivered" },
  cancelled: { bg: "bg-red-500/15",    text: "text-red-400",    label: "Cancelled" },
};

const CARRIERS = [
  { id: "usps", label: "USPS" },
  { id: "ups", label: "UPS" },
  { id: "fedex", label: "FedEx" },
  { id: "dhl", label: "DHL" },
  { id: "shopify", label: "Shopify Fulfillment" },
  { id: "other", label: "Other" },
];

const ShipModal = ({ order, onClose, onSaved }) => {
  const [carrier, setCarrier] = useState(order.carrier || "usps");
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (statusOverride) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.order_id}`, {
        status: statusOverride || "shipped",
        carrier,
        tracking_number: trackingNumber,
      });
      toast.success(statusOverride === "shipped" || !statusOverride ? "Marked as shipped — tracking email sent." : `Order marked ${statusOverride}.`);
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update order.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="admin-ship-modal">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#0B0B0B] border border-[#C0953A]/30"
      >
        <div className="flex items-start justify-between p-6 border-b border-[#C0953A]/20">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C0953A]">Fulfil Order</p>
            <h2 className="font-serif-d text-2xl text-[#FAF4E9] mt-1">{order.product_name}</h2>
            <p className="text-xs text-[#FAF4E9]/60 mt-1">Size {order.size} · Qty {order.quantity} · ${(order.amount || 0).toFixed(2)}</p>
          </div>
          <button data-testid="admin-ship-close" onClick={onClose} className="text-[#FAF4E9]/60 hover:text-[#FAF4E9]"><X size={20} /></button>
        </div>

        <div className="p-6 text-[#FAF4E9] space-y-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#FAF4E9]/50 mb-2">Carrier</p>
            <select
              data-testid="admin-ship-carrier"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="w-full bg-transparent border border-[#FAF4E9]/25 focus:border-[#C0953A] px-3 py-3 text-sm outline-none"
            >
              {CARRIERS.map((c) => <option key={c.id} value={c.id} className="bg-[#0B0B0B]">{c.label}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#FAF4E9]/50 mb-2">Tracking number</p>
            <input
              data-testid="admin-ship-tracking"
              placeholder="e.g. 9400136106212345678900"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="w-full bg-transparent border border-[#FAF4E9]/25 focus:border-[#C0953A] px-3 py-3 text-sm outline-none"
            />
            <p className="mt-2 text-[10px] text-[#FAF4E9]/40">Customer will get a &ldquo;Your crown just shipped&rdquo; email with the tracking link the moment you save.</p>
          </div>

          <div className="pt-4 border-t border-[#C0953A]/15 space-y-2 text-xs text-[#FAF4E9]/70">
            <p className="text-[10px] uppercase tracking-widest text-[#C0953A]">Ship to</p>
            <p>{order.shipping_address?.name}</p>
            <p>{order.shipping_address?.address_line1}{order.shipping_address?.address_line2 ? `, ${order.shipping_address.address_line2}` : ""}</p>
            <p>{order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}</p>
            <p className="text-[#FAF4E9]/50">{order.shipping_address?.email}</p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              data-testid="admin-ship-cancel"
              onClick={() => { if (window.confirm(`Cancel order ${order.order_id?.slice(-8)}? This cannot be undone.`)) handleSubmit("cancelled"); }}
              disabled={saving}
              className="flex-1 border border-red-400/30 text-red-400 hover:bg-red-400/10 uppercase tracking-[0.14em] text-[10px] py-3 disabled:opacity-50"
            >Cancel Order</button>
            <button
              data-testid="admin-ship-save"
              onClick={() => handleSubmit("shipped")}
              disabled={saving}
              className="flex-[2] bg-[#C0953A] hover:bg-[#DCBB6C] text-black uppercase tracking-[0.18em] text-xs font-semibold py-3 disabled:opacity-50"
            >{saving ? "Saving…" : "Mark Shipped & Email →"}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function AdminOrders() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = "Admin · Orders";
    if (user?.role === "admin") {
      api.get("/admin/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  if (authLoading || loading) {
    return <div className="min-h-screen bg-[#0B0B0B] text-[#C0953A] flex items-center justify-center">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const fmt = (d) => { try { return new Date(d).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

  const counts = orders.reduce((acc, o) => ({ ...acc, [o.status]: (acc[o.status] || 0) + 1, all: (acc.all || 0) + 1 }), {});

  const handleSaved = (updated) => {
    setOrders((prev) => prev.map((o) => (o.order_id === updated.order_id ? updated : o)));
    setSelected(null);
  };

  const trackUrl = (o) => {
    if (!o.tracking_number || !o.carrier) return "";
    const map = {
      usps: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${o.tracking_number}`,
      ups: `https://www.ups.com/track?tracknum=${o.tracking_number}`,
      fedex: `https://www.fedex.com/fedextrack/?trknbr=${o.tracking_number}`,
      dhl: `https://www.dhl.com/en/express/tracking.html?AWB=${o.tracking_number}`,
    };
    return map[o.carrier] || "";
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-[#FAF4E9]">
      <Nav />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow text-[#C0953A]">Admin · Haus of Nata</p>
            <h1 className="font-serif-d text-4xl sm:text-5xl mt-3 flex items-center gap-3"><Package className="text-[#C0953A]" size={36} /> Orders</h1>
            <p className="mt-3 text-[#FAF4E9]/60 text-sm">Every purchase from your in-app store — fulfil, track, and notify.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {["all", "paid", "shipped", "delivered", "cancelled"].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] border ${filter === s ? "bg-[#C0953A] text-black border-[#C0953A]" : "border-[#FAF4E9]/20 text-[#FAF4E9]/70 hover:border-[#C0953A]"}`}
              >{s} {counts[s] ? `(${counts[s]})` : ""}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-16 border border-dashed border-[#C0953A]/25 p-16 text-center">
            <Filter className="mx-auto text-[#C0953A]/50" size={28} />
            <p className="mt-4 text-[#FAF4E9]/60">No orders in this view yet.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-4" data-testid="admin-orders-list">
            {filtered.map((o) => {
              const s = STATUS_COLORS[o.status] || STATUS_COLORS.paid;
              return (
                <div key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="bg-[#111] border border-[#C0953A]/20 hover:border-[#C0953A]/50 transition-colors p-6">
                  <div className="grid md:grid-cols-4 gap-6">
                    <div className="md:col-span-2">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#C0953A]/70">#{o.order_id?.slice(-8)} · {fmt(o.created_at)}</p>
                      <h3 className="font-serif-d text-2xl mt-1">{o.product_name}</h3>
                      <p className="text-xs text-[#FAF4E9]/60 mt-1">Size {o.size} · Qty {o.quantity}</p>
                      <p className="text-sm text-[#FAF4E9]/80 mt-3">
                        {o.shipping_address?.name} · {o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.postal_code}
                      </p>
                      <p className="text-xs text-[#FAF4E9]/40">{o.shipping_address?.email}</p>
                      {o.tracking_number && (
                        <p className="mt-3 text-xs text-[#DCBB6C]">
                          <Truck size={12} className="inline mr-1" /> {o.carrier?.toUpperCase()} · {o.tracking_number}
                          {trackUrl(o) && <a href={trackUrl(o)} target="_blank" rel="noopener noreferrer" className="ml-2 underline hover:text-[#FAF4E9]"><ExternalLink size={11} className="inline" /></a>}
                        </p>
                      )}
                    </div>
                    <div className="text-left md:text-right">
                      <p className="font-serif-d text-2xl text-[#DCBB6C]">${(o.amount || 0).toFixed(2)}</p>
                      <span className={`inline-block mt-2 px-3 py-1 text-[10px] uppercase tracking-widest ${s.bg} ${s.text}`}>{s.label}</span>
                    </div>
                    <div className="flex md:justify-end items-start">
                      {o.status !== "delivered" && o.status !== "cancelled" && (
                        <button
                          data-testid={`admin-ship-btn-${o.order_id}`}
                          onClick={() => setSelected(o)}
                          className="bg-[#C0953A] hover:bg-[#DCBB6C] text-black uppercase tracking-[0.16em] text-[11px] font-semibold px-5 py-3 whitespace-nowrap"
                        >{o.status === "shipped" ? "Update Tracking" : "Fulfil →"}</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {selected && <ShipModal order={selected} onClose={() => setSelected(null)} onSaved={handleSaved} />}
    </div>
  );
}
